/**
 * useDocuments — React Query hook returning the tenant's data room
 * documents (sensitivity-filtered by the server per the caller's role +
 * tier per CHAP-95).
 *
 * The download flow is a two-step click:
 *   1. User clicks a download row → POST to /signed-url endpoint
 *   2. Server writes access_log row + issues 15-min signed URL
 *   3. Client window.location.href = url → browser downloads from R2
 *
 * The signed URL is single-use (Content-Disposition forces download +
 * R2 invalidates after the 15-min TTL anyway). Don't cache the URLs
 * client-side; refetch per click.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch } from '../lib/api.js';
import type {
  DataroomDocument,
  DataroomDocumentList,
} from '../../shared/types.js';

/**
 * List of documents visible to the current user (sensitivity-filtered
 * server-side; hiddenCount is the delta between total + visible).
 */
export function useDocuments(
  slug: string,
): UseQueryResult<DataroomDocumentList, Error> {
  return useQuery<DataroomDocumentList, Error>({
    queryKey: ['documents', slug],
    queryFn: () => apiGet<DataroomDocumentList>(`/api/c/${slug}/documents`),
    enabled: Boolean(slug),
    staleTime: 60_000, // 1 min — documents don't change often
  });
}

/**
 * Trigger a signed-URL download for one document. Returns a function
 * that, when called, fetches the signed URL + redirects the browser to
 * it. Errors throw; UI shows a toast.
 */
export function useDownloadDocument(slug: string): UseMutationResult<
  { url: string; expiresAt: string },
  Error,
  string // docId
> {
  return useMutation({
    mutationFn: async (docId: string) => {
      return apiGet<{ url: string; expiresAt: string }>(
        `/api/c/${slug}/documents/${docId}/signed-url`,
      );
    },
    onSuccess: ({ url }) => {
      // Redirect the browser to the signed URL. Content-Disposition on
      // the URL (set server-side per the downloadFilename option in CHAP-95)
      // forces a download rather than rendering in-tab.
      window.location.href = url;
    },
  });
}

/**
 * Edit document metadata (admin/team only). Server enforces role check.
 */
export interface EditDocumentInput {
  name?: string;
  description?: string | null;
  sensitivityTier?: DataroomDocument['sensitivityTier'];
  provenanceTier?: DataroomDocument['provenanceTier'];
  category?: DataroomDocument['category'];
  sourceAttribution?: string;
  isPrimary?: boolean;
  requiresExtraction?: boolean;
  dateOfRecord?: string | null;
  /** Required when demoting FROM 'authoritative' (>= 10 chars per CHAP-97 AC) */
  reason?: string;
}

export function useEditDocument(
  slug: string,
): UseMutationResult<
  { document: DataroomDocument },
  Error,
  { docId: string; edits: EditDocumentInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ docId, edits }) =>
      apiPatch<{ document: DataroomDocument }>(
        `/api/c/${slug}/documents/${docId}`,
        edits,
      ),
    onSuccess: () => {
      // Invalidate the list query so the edit shows up
      qc.invalidateQueries({ queryKey: ['documents', slug] });
    },
  });
}

/**
 * Upload a file (admin/team only). Two-step:
 *   1. POST /upload-token with filename + contentType + sizeBytes
 *      → returns { url, r2Key, expiresAt }
 *   2. PUT the file body to the signed url with the EXACT contentType
 *      (signed signature includes content-type binding per CHAP-75)
 *   3. POST to /documents with r2Key + metadata → creates the row
 *
 * onProgress is called with bytes uploaded so the UI can show a bar.
 */
export interface UploadInput {
  file: File;
  sensitivityTier?: DataroomDocument['sensitivityTier'];
  provenanceTier?: DataroomDocument['provenanceTier'];
  category?: DataroomDocument['category'];
  description?: string;
  sourceAttribution?: string;
  onProgress?: (bytesUploaded: number, totalBytes: number) => void;
}

export function useUploadDocument(
  slug: string,
): UseMutationResult<{ document: DataroomDocument }, Error, UploadInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, onProgress, ...metadata }) => {
      // Step 1: get signed upload URL
      const token = await apiPost<{
        url: string;
        r2Key: string;
        expiresAt: string;
        contentType: string;
      }>(`/api/c/${slug}/documents/upload-token`, {
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
      });

      // Step 2: PUT the file body. XHR for progress tracking; fetch
      // doesn't expose upload progress yet.
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', token.url);
        xhr.setRequestHeader('Content-Type', token.contentType);
        if (onProgress) {
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) onProgress(e.loaded, e.total);
          });
        }
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`R2 PUT failed: ${xhr.status} ${xhr.statusText}`));
        };
        xhr.onerror = () => reject(new Error('R2 PUT network error'));
        xhr.send(file);
      });

      // Step 3: create the row
      return apiPost<{ document: DataroomDocument }>(
        `/api/c/${slug}/documents`,
        {
          r2Key: token.r2Key,
          filename: file.name,
          contentType: token.contentType,
          sizeBytes: file.size,
          ...metadata,
        },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents', slug] });
    },
  });
}
